import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import Sidebar from "./component/Sidebar";
import Box from "@mui/material/Box";
import TablePagination from "@mui/material/TablePagination";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Ads_fulltitle } from "../_helpers/ads_fullname";
import {
  Breadcrumbs,
  Stack,
  Button,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import Link from "@mui/material/Link";
import InputBase from "@mui/material/InputBase";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { styled } from "@mui/material/styles";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { alertActions } from "../_actions";
import ToastMessageComp from "../component/ToastMessageComp";
import { Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageLoaderComp from "../component/pageLoaderComp";

const breadcrumbs = [
  <Link underline="hover" key="1" color="inherit" href="/">
    Tableau de bord
  </Link>,
  <Link underline="hover" key="2" color="inherit" href="/admin/devis">
    Gestion des commandes
  </Link>,
  <Typography key="3">Annonces Annulées</Typography>,
];

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));


function EnhancedTableToolbar(props) {
  const { setSearchQuery } = props;
  const [searchInput, setSearchInput] = useState("");
  
  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchInput, setSearchQuery]);
  
  const handleSearch = (event) => {
    setSearchInput(event.target.value);
  };
  
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%", marginTop: "15px" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        <Paper
          className="xl:w-[400px] w-full"
          component="form"
          sx={{
            p: "2px 4px",
            display: "flex",
            alignItems: "center", 
          }}
        >
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Rechercher..."
            inputProps={{ "aria-label": "search " }}
            value={searchInput}
            onChange={handleSearch}
          />

          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <IconButton type="button" sx={{ p: "10px" }} aria-label="search" disabled>
            <SearchIcon />
          </IconButton>
        </Paper>
      </Typography>
    </Toolbar>
  );
}

export default function AnnoncesAnnuleesComp() {
  const currentAdmin = useSelector((state) => state.administrator.admin);
  const alert = useSelector((state) => state.alert);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const [order, setOrder] = React.useState("DESC");
  const [orderBy, setOrderBy] = React.useState("id");

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [headCells, setHeadCells] = useState([
    {
      id: "id",
      numeric: false,
      disablePadding: true,
      label: "ID",
    },
    {
      id: "annonces_id",
      numeric: true,
      disablePadding: false,
      label: "Denomination",
    },
    {
      id: "cancel_reason",
      numeric: true,
      disablePadding: false,
      label: "Raison d'annulation",
    },
    {
      id: "created_at",
      numeric: true,
      disablePadding: false,
      label: "Date d'annulation",
    },
    {
      id: "Actions",
      numeric: true,
      disablePadding: false,
      label: "Actions",
    },
  ]);
  
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Remove client-side pagination and sorting since we're using server-side
  const visibleRows = rows;
  
  const getAllCanceledAnnouncements = useCallback(async () => {
    setPageLoading(true);
    try {
      const params = {
        page: page + 1, // API usually expects 1-based page numbers
        limit: rowsPerPage,
        search: searchQuery,
        sortBy: orderBy,
        sortOrder: order
      };
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/announce/canceled`,
        { params }
      );
      
      setRows(response.data.data || []);
      setTotalCount(response.data.pagination.totalItems || response.data.data?.length || 0);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      console.error(error);
      // Fallback for backward compatibility
      setRows([]);
      setTotalCount(0);
    }
  }, [page, rowsPerPage, searchQuery, orderBy, order]);
  
  useEffect(() => {
    getAllCanceledAnnouncements();
  }, [getAllCanceledAnnouncements]);
  
  const handleRequestSort = useCallback((event, property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
  }, [order, orderBy]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Server-side pagination doesn't need empty rows calculation
  const emptyRows = 0;

  const [anchorEls, setAnchorEls] = useState({});

  const handleClick1 = (event, id) => {
    setAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleClose1 = (id) => {
    setAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  const handleViewDetails = (row) => {
    setSelectedAnnouncement(row);
    setOpenDetailDialog(true);
    handleClose1(row.id);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedAnnouncement(null);
  };

  if (!currentAdmin) {
    return <Navigate to="/admin/login" />;
  }

  return (
    <>
      {successful ? (
        <ToastMessageComp message={alert.message} type={alert.type} />
      ) : (
        ""
      )}
      {pageLoading ? <PageLoaderComp /> : ""}

      <div className=" w-full h-full bg-white p-8">
        <Typography
          variant="h4"
          component="h4"
          fontWeight={500}
          color={"#1CBE93"}
        >
          Annonces Annulées{" "}
        </Typography>
        <Stack spacing={2} mb={3}>
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            {breadcrumbs}
          </Breadcrumbs>
        </Stack>
        
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/devis')}
            sx={{ mr: 2 }}
          >
            Retour aux commandes
          </Button>
        </Box>
        
        <Box sx={{ width: "100%" }}>
          <Paper sx={{ width: "100%", mb: 2 }}>
            <EnhancedTableToolbar setSearchQuery={setSearchQuery} />
            <div style={{ marginTop: "15px", overflowX: "auto" }}>
              <table 
                style={{ 
                  width: "100%", 
                  minWidth: "750px", 
                  borderCollapse: "collapse",
                  backgroundColor: "white"
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    {headCells.map((headCell) => (
                      <th
                        key={headCell.id}
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          borderBottom: "1px solid #e0e0e0",
                          color: "#797979",
                          fontSize: "16px",
                          fontWeight: "400",
                          cursor: "pointer"
                        }}
                        onClick={() => handleRequestSort(null, headCell.id)}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {headCell.label}
                          {orderBy === headCell.id && (
                            <span style={{ marginLeft: "4px" }}>
                              {order === "desc" ? "↓" : "↑"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                  let annonces_data = JSON.parse(row.annonces_data);
                  let votre_annonce=JSON.parse(annonces_data.votre_annonce);
                  let denomination=votre_annonce.denomination || votre_annonce.denomination_societe || votre_annonce.socitename;

                    return (
                      <tr
                        key={row.id}
                        style={{
                          cursor: "pointer",
                          borderBottom: "1px solid #e0e0e0"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "16px",
                            fontWeight: "normal",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {row.annonces_id}
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "16px",
                            fontWeight: "normal",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {denomination}
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "16px",
                            fontWeight: "normal",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {row.cancel_reason || 'Non spécifiée'}
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "16px",
                            fontWeight: "normal",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {moment(row.created_at).format("LL")}
                        </td>
                        <td style={{ padding: "16px", textAlign: "center" }}>
                          <div key={row.id}>
                            <IconButton
                              aria-label="more"
                              aria-controls={`long-menu-${row.id}`}
                              aria-haspopup="true"
                              onClick={(e) => handleClick1(e, row.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              id={`long-menu-${row.id}`}
                              anchorEl={anchorEls[row.id]}
                              keepMounted
                              open={Boolean(anchorEls[row.id])}
                              onClose={() => handleClose1(row.id)}
                            >
                              <MenuItem
                                key={"voir détails"}
                                onClick={() => handleViewDetails(row)}
                              >
                                <VisibilityIcon color={"primary"} />
                                &nbsp;&nbsp;{"Voir détails"}
                              </MenuItem>
                            </Menu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                 {rows.length <= 0 && (
                    <tr>
                      <td 
                        colSpan={5}
                        style={{
                          padding: "32px",
                          textAlign: "center",
                          fontSize: "18px",
                          fontWeight: "bold",
                          textTransform: "capitalize"
                        }}
                      >
                        Aucune annonce annulée trouvée!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
      </div>

      {/* Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Détails de l'annonce annulée</DialogTitle>
        <DialogContent>
          {selectedAnnouncement && (
            <div>
              <Typography variant="h6" gutterBottom>
                Informations générales
              </Typography>
              <Typography><strong>ID:</strong> {selectedAnnouncement.id}</Typography>
              <Typography><strong>ID Annonce:</strong> {selectedAnnouncement.annonces_id}</Typography>
              <Typography><strong>Raison d'annulation:</strong> {selectedAnnouncement.cancel_reason || 'Non spécifiée'}</Typography>
              <Typography><strong>Date d'annulation:</strong> {moment(selectedAnnouncement.created_at).format("LL")}</Typography>
              
              {selectedAnnouncement.annonces_data && (() => {
                let annoncesData; 
                let denomination;
                try {
                    annoncesData = JSON.parse(selectedAnnouncement.annonces_data);
                    var votre_annonce=JSON.parse(annoncesData.votre_annonce);
                    denomination=votre_annonce.denomination || votre_annonce.denomination_societe || votre_annonce.socitename;
                   
                } catch (error) {
                    console.error("Invalid JSON in annonces_data:", error);
                    annoncesData = null;
                }

                return (
                    <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Données de l'annonce
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                            <Typography><strong>Denomination:</strong> {denomination?denomination:''}</Typography>
                            <Typography><strong>Add Type:</strong> {annoncesData.add_type?Ads_fulltitle(annoncesData.add_type):''}</Typography>
                            <Typography><strong>Invoice Number:</strong> {annoncesData.invoice_number?annoncesData.invoice_number:''}</Typography>
                            <Typography><strong>Date Parution:</strong> {annoncesData.date_parution?moment(annoncesData.date_parution).format("LL"):''}</Typography>
                            <Typography>
                                <strong>Votre PDF:</strong>{" "}
                                {annoncesData?.votre_pdf && (
                                    <a
                                    href={`${process.env.REACT_APP_AWS_URL_STATIC_BUSYPLACE}assets/uploads/pdfgenerate/invoice/${annoncesData.votre_pdf}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                    <PictureAsPdfIcon
                                        sx={{ color: "red", verticalAlign: "middle", fontSize: 28 }}
                                    />
                                    </a>
                                )}
                            </Typography>
                            <Typography>
                                <strong>Attestation PDF:</strong>{" "}
                                {annoncesData?.attestation && (
                                    <a
                                    href={`${process.env.REACT_APP_AWS_URL_STATIC_BUSYPLACE}assets/uploads/pdfgenerate/attestation/${annoncesData.attestation}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                    <PictureAsPdfIcon
                                        sx={{ color: "red", verticalAlign: "middle", fontSize: 28 }}
                                    />
                                    </a>
                                )}
                            </Typography>

                            <Typography><strong>Votre HTML:</strong> 
                             <div className=' rounded-[14px] p-5 sm:mx-0 mx-2' style={{ 'boxShadow': '0px 4px 4px rgba(0, 0, 0, 0.25)' }} dangerouslySetInnerHTML={{__html:annoncesData.votre_html?annoncesData.votre_html:''}}></div>     
                            </Typography>
                            
                        </pre>
                    </Paper>
                    </>
                );
                })()}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
